import React, {useEffect, useRef, useState} from 'react';
import {Box, List, Scroller} from '@tempo/core';
import SearchResult from './SearchResult';
import type {RenderResultFunction, SearchResultKeyExtractor} from './types';

type SearchResultListProps<T> = {
	id: string | undefined;
	results?: T[];
	isLoading?: boolean;
	error?: unknown;
	renderSearchResult: RenderResultFunction<T>;
	renderSkeleton: () => React.ReactElement;
	renderPlaceholder: () => React.ReactElement;
	renderSearchError?: () => React.ReactElement;
	keyExtractor: SearchResultKeyExtractor<T>;
	onSelectResult: (item: T, index: number) => void;
};

const SearchResultList = <T,>({
	id,
	results,
	isLoading = false,
	error,
	renderSearchResult,
	renderSkeleton,
	renderPlaceholder,
	renderSearchError,
	keyExtractor,
	onSelectResult,
}: SearchResultListProps<T>) => {
	const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);
	const [debouncedIsLoading, setDebouncedIsLoading] = useState(isLoading);

	useEffect(() => {
		if (loadingTimeoutRef.current !== undefined) {
			window.clearTimeout(loadingTimeoutRef.current);
			loadingTimeoutRef.current = undefined;
		}

		loadingTimeoutRef.current = setTimeout(
			() => {
				setDebouncedIsLoading(isLoading);
				loadingTimeoutRef.current = undefined;
			},
			isLoading ? 200 : 0,
		);

		return () => {
			if (loadingTimeoutRef.current !== undefined) {
				clearTimeout(loadingTimeoutRef.current);
				loadingTimeoutRef.current = undefined;
			}
		};
	}, [isLoading]);

	if (!debouncedIsLoading && typeof results === 'undefined' && !error) {
		return null;
	}

	let content: React.ReactNode;

	if (debouncedIsLoading) {
		content = (
			<Box py={1} px={3}>
				{renderSkeleton()}
			</Box>
		);
	} else if (error) {
		content = <Box>{renderSearchError ? renderSearchError() : null}</Box>;
	} else if (typeof results === 'undefined' || results.length === 0) {
		content = <Box>{renderPlaceholder()}</Box>;
	} else {
		content = results.map((item, index) => {
			const key = keyExtractor(item, index);

			return (
				<SearchResult
					data-testid={`${id}-option-${index}`}
					key={key}
					id={`search-result-${key}`}
					item={item}
					index={index}
					renderSearchResult={renderSearchResult}
					onClick={onSelectResult}
				/>
			);
		});
	}

	return (
		<Box
			bg="background.neutral.secondary.default"
			w="100%"
			top="calc(100% + 4px)"
			position="absolute"
			left={0}
			p={1}
			zIndex="popover"
			boxShadow="md"
			borderRadius="base"
		>
			<Scroller maxHeight="190px" w="100%">
				<List spacing={1} w="100%">
					{content}
				</List>
			</Scroller>
		</Box>
	);
};

export default SearchResultList;
